import { expectRecord, expectString } from "./schema";
import { parseStudentBillingCurrency, parseStudentBillingIdentifier, parseStudentBillingServiceMonth } from "./student-billing";
export const STUDENT_BILLING_MANUAL_TARGET_TYPES = ["student", "lesson"];
export const STUDENT_BILLING_MANUAL_SOURCE_TYPES = ["individualManual", "lessonManual"];
export const STUDENT_BILLING_MANUAL_STATUSES = ["active"];
function exact(record, allowed, path) {
    const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
    if (unknown.length > 0)
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}
function enumValue(value, allowed, path) {
    const parsed = expectString(value, path);
    if (!allowed.includes(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
export function parseStudentBillingSignedNonZeroAmount(value, path = "amount") {
    if (!Number.isSafeInteger(value) || value === 0)
        throw new Error(`${path} must be a signed non-zero safe integer`);
    return value;
}
export function parseStudentBillingManualReason(value, path = "reason") {
    const parsed = expectString(value, path).trim();
    if (parsed.length < 1 || parsed.length > 200)
        throw new Error(`${path} must contain 1-200 characters`);
    if (/[<>]/u.test(parsed))
        throw new Error(`${path} must not contain raw HTML`);
    return parsed;
}
export function parseStudentBillingTargetFingerprint(value, path = "targetFingerprint") {
    const parsed = expectString(value, path);
    if (!/^[a-f0-9]{64}$/u.test(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
function parseTarget(value, path) {
    const record = expectRecord(value, path);
    const type = enumValue(record.type, STUDENT_BILLING_MANUAL_TARGET_TYPES, `${path}.type`);
    if (type === "student") {
        exact(record, ["type", "studentId"], path);
        return { type, studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`) };
    }
    exact(record, ["type", "lessonId"], path);
    return { type, lessonId: parseStudentBillingIdentifier(record.lessonId, `${path}.lessonId`) };
}
function parsePreviewFields(record, path) {
    return {
        target: parseTarget(record.target, `${path}.target`),
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        amount: parseStudentBillingSignedNonZeroAmount(record.amount, `${path}.amount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        reason: parseStudentBillingManualReason(record.reason, `${path}.reason`)
    };
}
export function parsePreviewStudentBillingManualSourceRequest(value, path = "manualSourcePreviewRequest") {
    const record = expectRecord(value, path);
    exact(record, ["target", "serviceMonth", "amount", "currency", "reason"], path);
    return parsePreviewFields(record, path);
}
export function parseCreateStudentBillingManualSourceRequest(value, path = "manualSourceCreateRequest") {
    const record = expectRecord(value, path);
    exact(record, ["target", "serviceMonth", "amount", "currency", "reason", "expectedTargetFingerprint"], path);
    return {
        ...parsePreviewFields(record, path),
        expectedTargetFingerprint: parseStudentBillingTargetFingerprint(record.expectedTargetFingerprint, `${path}.expectedTargetFingerprint`)
    };
}
function isoString(value, path) {
    const parsed = expectString(value, path);
    if (!Number.isFinite(new Date(parsed).getTime()))
        throw new Error(`${path} is invalid`);
    return new Date(parsed).toISOString();
}
function nullableIdentifier(value, path) {
    return value === null ? null : parseStudentBillingIdentifier(value, path);
}
export function parseStudentBillingManualBatch(value, path = "manualBatch") {
    const record = expectRecord(value, path);
    exact(record, [
        "batchId", "tenantId", "targetType", "sourceStudentId", "sourceLessonId", "sourceLessonTitle",
        "serviceMonth", "amountPerStudent", "currency", "reason", "targetCount", "targetFingerprint",
        "status", "createdByPlatformUserId", "createdAt", "updatedAt"
    ], path);
    const targetType = enumValue(record.targetType, STUDENT_BILLING_MANUAL_TARGET_TYPES, `${path}.targetType`);
    const sourceStudentId = nullableIdentifier(record.sourceStudentId, `${path}.sourceStudentId`);
    const sourceLessonId = nullableIdentifier(record.sourceLessonId, `${path}.sourceLessonId`);
    const sourceLessonTitle = record.sourceLessonTitle === null ? null : expectString(record.sourceLessonTitle, `${path}.sourceLessonTitle`).trim();
    if (targetType === "student" && (sourceStudentId === null || sourceLessonId !== null || sourceLessonTitle !== null)) {
        throw new Error(`${path} student target binding is invalid`);
    }
    if (targetType === "lesson" && (sourceStudentId !== null || sourceLessonId === null || !sourceLessonTitle)) {
        throw new Error(`${path} lesson target binding is invalid`);
    }
    if (!Number.isSafeInteger(record.targetCount) || Number(record.targetCount) < 1 || Number(record.targetCount) > 100) {
        throw new Error(`${path}.targetCount is invalid`);
    }
    return {
        batchId: parseStudentBillingIdentifier(record.batchId, `${path}.batchId`),
        tenantId: parseStudentBillingIdentifier(record.tenantId, `${path}.tenantId`),
        targetType,
        sourceStudentId,
        sourceLessonId,
        sourceLessonTitle,
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        amountPerStudent: parseStudentBillingSignedNonZeroAmount(record.amountPerStudent, `${path}.amountPerStudent`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        reason: parseStudentBillingManualReason(record.reason, `${path}.reason`),
        targetCount: Number(record.targetCount),
        targetFingerprint: parseStudentBillingTargetFingerprint(record.targetFingerprint, `${path}.targetFingerprint`),
        status: enumValue(record.status, STUDENT_BILLING_MANUAL_STATUSES, `${path}.status`),
        createdByPlatformUserId: parseStudentBillingIdentifier(record.createdByPlatformUserId, `${path}.createdByPlatformUserId`),
        createdAt: isoString(record.createdAt, `${path}.createdAt`),
        updatedAt: isoString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseStudentBillingManualItem(value, path = "manualItem") {
    const record = expectRecord(value, path);
    exact(record, [
        "manualItemId", "tenantId", "batchId", "studentId", "sourceType", "sourceLessonId",
        "serviceMonth", "amount", "currency", "reason", "status", "createdAt", "updatedAt"
    ], path);
    const sourceType = enumValue(record.sourceType, STUDENT_BILLING_MANUAL_SOURCE_TYPES, `${path}.sourceType`);
    const sourceLessonId = nullableIdentifier(record.sourceLessonId, `${path}.sourceLessonId`);
    if ((sourceType === "individualManual" && sourceLessonId !== null) || (sourceType === "lessonManual" && sourceLessonId === null)) {
        throw new Error(`${path} source binding is invalid`);
    }
    return {
        manualItemId: parseStudentBillingIdentifier(record.manualItemId, `${path}.manualItemId`),
        tenantId: parseStudentBillingIdentifier(record.tenantId, `${path}.tenantId`),
        batchId: parseStudentBillingIdentifier(record.batchId, `${path}.batchId`),
        studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
        sourceType,
        sourceLessonId,
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        amount: parseStudentBillingSignedNonZeroAmount(record.amount, `${path}.amount`),
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        reason: parseStudentBillingManualReason(record.reason, `${path}.reason`),
        status: enumValue(record.status, STUDENT_BILLING_MANUAL_STATUSES, `${path}.status`),
        createdAt: isoString(record.createdAt, `${path}.createdAt`),
        updatedAt: isoString(record.updatedAt, `${path}.updatedAt`)
    };
}
